import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { dataStore } from '../stores/dataStore'

// Per-language string overrides applied after automatic translation. Use
// this when the translation service produces a term we want to standardise
// differently for a specific locale (e.g. preferring the traditional
// Cantonese rendering of "Beaufort" over the Mandarin-leaning transliteration).
// Applied on every translated string the service writes back into i18n,
// so cached + freshly-fetched translations both pick up the correction.
const WORD_OVERRIDES: Record<string, Array<[RegExp, string]>> = {
  'zh-hk': [
    [/博福特/g, '蒲福氏'],
  ],
}

// Per-language, per-key full-string overrides. Keys use the dotted i18n path
// (e.g. "about.attributions"). Applied after WORD_OVERRIDES so a key-level
// override always wins. Use this when a specific label has a preferred
// translation that AWS Translate doesn't pick (e.g. "Attributions" → 來源
// rather than the default 屬性/歸屬 that the auto-translator tends to emit).
//
// AWS Translate renders "passkey" / "Passkey" as 密碼 in Chinese, which
// collides with the term for "password" — and the same dialog deliberately
// contrasts both concepts ("…no passkeys yet. Add one to skip the password
// next time."). So we can't blanket-replace via WORD_OVERRIDES; only the
// keys whose English source contains the word "passkey" get overridden.
const PASSKEY_ZH: Record<string, string> = {
  'settings.passkeys':              '通行密鑰',
  'auth.titles.passkey':            '使用通行密鑰登入',
  'auth.titles.setPasskey':         '設定通行密鑰',
  'auth.signInWithPasskey':         '使用通行密鑰登入',
  'auth.setPasskeyCta':             '設定通行密鑰',
  'auth.errors.passkeySignIn':      '通行密鑰登入失敗',
  'auth.errors.passkeySetup':       '通行密鑰設定失敗',
  'passkeysDialog.fallbackName':    '通行密鑰',
  'passkeysDialog.empty':           '尚無通行密鑰。新增一個，下次就不必輸入密碼。',
  'passkeysDialog.added':           '已新增通行密鑰。下次可使用它登入 — 包括所有同步通行密鑰的裝置。',
  'passkeysDialog.addCta':          '新增通行密鑰',
  'passkeysDialog.errors.load':     '無法載入通行密鑰',
  'passkeysDialog.errors.add':      '無法新增通行密鑰',
  'passkeysDialog.errors.remove':   '無法移除通行密鑰',
}

const KEY_OVERRIDES: Record<string, Record<string, string>> = {
  'zh':    {
    'about.attributions': '來源',
    'forecast.chartLegend.humidity': '相對濕度 (%)',
    ...PASSKEY_ZH,
  },
  'zh-tw': {
    'about.attributions': '來源',
    'forecast.chartLegend.humidity': '相對濕度 (%)',
    ...PASSKEY_ZH,
  },
  'zh-hk': {
    'about.attributions': '來源',
    'forecast.chartLegend.humidity': '相對濕度 (%)',
    ...PASSKEY_ZH,
  },
}

// Sanitize + apply per-language overrides to translation messages. Walks the
// i18n messages tree tracking dotted key paths so KEY_OVERRIDES can match
// specific entries like "about.attributions". A key-level override always
// wins over WORD_OVERRIDES — if both apply, the key-level value is used
// verbatim and word substitutions are skipped.
function sanitizeMessages(messages: any, lang?: string, keyPath = ''): any {
  if (messages === null || typeof messages !== 'object') {
    return messages
  }

  const wordRules = lang ? WORD_OVERRIDES[lang] : undefined
  const keyRules = lang ? KEY_OVERRIDES[lang] : undefined
  return Object.keys(messages).reduce((acc, key) => {
    const path = keyPath ? `${keyPath}.${key}` : key
    const value = messages[key]
    if (typeof value === 'string') {
      if (keyRules && path in keyRules) {
        acc[key] = keyRules[path]
      } else {
        let next = value.replace(/{年度}/g, '{year}')
        if (wordRules) {
          for (const [pattern, replacement] of wordRules) {
            next = next.replace(pattern, replacement)
          }
        }
        acc[key] = next
      }
    } else {
      acc[key] = sanitizeMessages(value, lang, path)
    }
    return acc
  }, {} as any)
}

// Translation cache
const translationCache = new Map<string, any>()

// Loading states
export const isTranslating = ref(false)
export const translationError = ref<string | null>(null)

// Translation service configuration
const TRANSLATION_API_URL = '/api/translate'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
const CACHE_VERSION = 'v9' // Increment to invalidate all caches
// Prebuilt packs come from a CDN; if one is not answering promptly the API
// fallback is a better use of the user's time than waiting.
const STATIC_PACK_TIMEOUT_MS = 4000

// Language detection utility
export const detectUserLanguage = (): string => {
  // Check localStorage first
  const savedLanguage = localStorage.getItem('language')
  if (savedLanguage) return savedLanguage

  // Check browser language
  const browserLang = navigator.language.toLowerCase()
  
  // Map browser language codes to our supported languages
  const langMap: Record<string, string> = {
    'en': 'en',
    'en-us': 'en',
    'en-gb': 'en',
    'zh': 'zh',
    'zh-cn': 'zh',
    'zh-hans': 'zh',
    'zh-hk': 'zh-hk',
    'zh-hant': 'zh-tw',
    'zh-tw': 'zh-tw',
    'hi': 'hi',
    'es': 'es',
    'fr': 'fr',
    'ar': 'ar',
    'bn': 'bn',
    'pt': 'pt',
    'ru': 'ru',
    'ja': 'ja',
    'de': 'de',
    'ko': 'ko',
    'it': 'it',
    'tr': 'tr',
    'pl': 'pl',
    'nl': 'nl',
    'sv': 'sv',
    'da': 'da',
    'no': 'no',
    'fi': 'fi',
    // 'el' is the correct code for Greek. This said 'gr' — the country
    // code for Greece, not a language code — so every Greek browser was
    // auto-detected into a 400 and silently served English (#204).
    'el': 'el',
    'he': 'he',
    'th': 'th',
    'vi': 'vi',
    'id': 'id',
    'ms': 'ms',
    'tl': 'tl',
    'uk': 'uk',
    'cs': 'cs',
    'sk': 'sk',
    'hu': 'hu',
    'ro': 'ro',
    'bg': 'bg',
    'hr': 'hr',
    'sr': 'sr',
    // 'sl' is Slovenian. This said 'si', which is SINHALA — a valid code,
    // so it failed silently and successfully: a Slovenian browser was
    // auto-detected straight into Sinhala (#204).
    'sl': 'sl',
    'et': 'et',
    'lv': 'lv',
    'lt': 'lt',
    'mt': 'mt'
  }

  return langMap[browserLang] || langMap[browserLang.split('-')[0]] || 'en'
}

// Translation service
export class TranslationService {
  private i18n: any
  private _loadingPromises: Map<string, Promise<void>> = new Map()

  constructor(i18nInstance: any) {
    this.i18n = i18nInstance
  }

  // Load translations for a specific language
  async loadTranslations(languageCode: string): Promise<void> {
    // Deduplicate concurrent requests for the same language
    const inflight = this._loadingPromises.get(languageCode)
    if (inflight) return inflight
    if (languageCode === 'en') {
      // English is already loaded as the base language
      this.i18n.global.locale.value = 'en'
      return
    }

    // Check if translations are already loaded in i18n
    if (this.i18n.global.messages.value[languageCode]) {
      this.i18n.global.locale.value = languageCode
      if (import.meta.env.DEV) console.log(`📚 Using pre-loaded translations for ${languageCode}`)
      return
    }

    // Check central data store first
    const cachedTranslations = dataStore.getTranslations(languageCode)
    if (cachedTranslations) {
      this.i18n.global.setLocaleMessage(languageCode, cachedTranslations)
      this.i18n.global.locale.value = languageCode
      if (import.meta.env.DEV) console.log(`📚 Using cached translations from data store for ${languageCode}`)
      return
    }

    // Check localStorage cache
    const localStorageKey = `wital_translations_${CACHE_VERSION}_${languageCode}`
    const cachedData = localStorage.getItem(localStorageKey)
    if (cachedData) {
      try {
        const translations = sanitizeMessages(JSON.parse(cachedData), languageCode)
        const cacheTimestamp = localStorage.getItem(`${localStorageKey}_timestamp`)
        const now = Date.now()
        const oneDay = 24 * 60 * 60 * 1000

        // Check if cached translations are missing any keys from the current English translations
        const currentEnglishKeys = this.flattenTranslations(this.i18n.global.messages.value.en)
        const cachedKeys = this.flattenTranslations(translations)
        const missingKeys = Object.keys(currentEnglishKeys).filter(key => !cachedKeys.hasOwnProperty(key))

        // Use cached data if it's less than 24 hours old AND has all current keys
        if (cacheTimestamp && (now - parseInt(cacheTimestamp)) < oneDay && missingKeys.length === 0) {
          dataStore.setTranslations(languageCode, translations)
          this.i18n.global.setLocaleMessage(languageCode, translations)
          this.i18n.global.locale.value = languageCode
          if (import.meta.env.DEV) console.log(`📚 Using cached translations for ${languageCode}`)
          return
        } else if (missingKeys.length > 0) {
          if (import.meta.env.DEV) console.log(`🔄 Cached translations missing ${missingKeys.length} keys, refreshing...`)
          // Remove old cache to force refresh
          localStorage.removeItem(localStorageKey)
          localStorage.removeItem(`${localStorageKey}_timestamp`)
        }
      } catch (error) {
        console.warn('Failed to parse cached translations:', error)
        localStorage.removeItem(localStorageKey)
        localStorage.removeItem(`${localStorageKey}_timestamp`)
      }
    }

    // Prebuilt static pack, shipped by scripts/build-locales.mjs and served
    // from CloudFront. This is the fast path for a first-time visitor in a
    // non-English locale: before it existed, the only way to get strings was
    // the /api/translate round trip below, which meant staring at the splash
    // screen while the whole table was translated on demand. See #204.
    const staticPack = await this.loadStaticPack(languageCode)
    if (staticPack) {
      dataStore.setTranslations(languageCode, staticPack)
      // Apply to i18n BEFORE persisting. localStorage.setItem throws
      // QuotaExceededError on a full quota and in Safari private mode, and
      // a ~30KB write is big enough to hit it. If that throw happened
      // first it would escape loadTranslations entirely — past the API
      // fallback — and leave the user with no strings at all.
      this.i18n.global.setLocaleMessage(languageCode, staticPack)
      this.i18n.global.locale.value = languageCode
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(staticPack))
        localStorage.setItem(`${localStorageKey}_timestamp`, Date.now().toString())
      } catch (err) {
        // Losing the cache costs a CDN fetch next time. Not worth failing.
        console.warn(`Could not cache locale pack for ${languageCode}:`, err)
      }
      if (import.meta.env.DEV) console.log(`📦 Using prebuilt locale pack for ${languageCode}`)
      return
    }

    // Try to fetch from AWS Lambda (if available), deduplicating concurrent calls
    const promise = this.fetchTranslationsFromAPI(languageCode).finally(() => {
      this._loadingPromises.delete(languageCode)
    })
    this._loadingPromises.set(languageCode, promise)
    await promise
  }

  /**
   * Load a prebuilt locale pack from /locales/<lang>.json.
   *
   * Returns null — never throws — when the pack is absent, stale against
   * the current English keys, or unparseable, so the caller falls through
   * to the live translation API exactly as it did before. A language with
   * no prebuilt pack is a normal case, not an error: the build only ships
   * packs for languages AWS Translate actually supports.
   */
  private async loadStaticPack(languageCode: string): Promise<any | null> {
    const url = `/locales/${encodeURIComponent(languageCode)}.json`
    // This is now the FIRST network call on the splash-screen critical
    // path, ahead of the API fallback which has its own 10s timeout. A
    // hung connection here would block startup indefinitely, which would
    // be strictly worse than the slow path this feature replaced.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), STATIC_PACK_TIMEOUT_MS)
    let resp: Response
    try {
      resp = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal })
    } catch (err) {
      // Absent pack, offline, aborted. All normal; the API path follows.
      return null
    } finally {
      clearTimeout(timer)
    }

    // Not every language has a pack — the build only ships languages the
    // translation backend actually supports — so a miss is routine.
    // CloudFront rewrites a missing file to the SPA shell with HTTP 200,
    // so judge by payload shape rather than status.
    if (!resp.ok) return null
    if (!(resp.headers.get('content-type') || '').includes('json')) return null

    try {
      const parsed = await resp.json()
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return this.rejectPack(languageCode, 'payload was not a JSON object')
      }

      const meta = parsed.__meta
      const { __meta, ...messages } = parsed

      const englishKeys = this.flattenTranslations(this.i18n.global.messages.value.en)
      const englishCount = Object.keys(englishKeys).length
      // A positive assertion, not just "nothing is missing". If the English
      // table were ever empty, a key-presence test would vacuously accept
      // any object at all — including {} — and the user would then read raw
      // dotted key paths for a whole day from localStorage.
      if (englishCount === 0) return null
      if (!meta || typeof meta.keyCount !== 'number') {
        return this.rejectPack(languageCode, 'pack has no __meta block; it predates the integrity check')
      }
      if (meta.keyCount !== englishCount) {
        // Routine after an en.json change; the pack simply needs rebuilding.
        if (import.meta.env.DEV) {
          console.log(`📦 Pack ${languageCode} has ${meta.keyCount} keys, English has ${englishCount} — rebuilding needed, using API`)
        }
        return null
      }

      const packKeys = this.flattenTranslations(messages)
      const missing = Object.keys(englishKeys).filter((k) => !(k in packKeys))
      if (missing.length > 0) {
        return this.rejectPack(
          languageCode,
          `count matched but ${missing.length} key(s) absent, e.g. ${missing.slice(0, 3).join(', ')}`,
        )
      }
      return sanitizeMessages(messages, languageCode)
    } catch (err) {
      return this.rejectPack(languageCode, `could not be parsed: ${err}`)
    }
  }

  /**
   * A pack that exists but is unusable is a build defect, not a normal
   * miss, so say so unconditionally rather than only in dev. The user
   * still gets strings via the API fallback, which means the only symptom
   * is "startup feels slow" — indistinguishable from the behaviour this
   * feature was built to remove, and therefore invisible without this log.
   */
  private rejectPack(languageCode: string, reason: string): null {
    console.error(`Prebuilt locale pack for ${languageCode} is unusable (${reason}); falling back to the translation API`)
    return null
  }

  // Fetch translations from AWS Lambda/API Gateway
  private async fetchTranslationsFromAPI(languageCode: string): Promise<void> {
    // Check if API endpoint is configured
    if (TRANSLATION_API_URL.includes('your-api-gateway-url')) {
      console.warn(`⚠️ Translation API not configured for ${languageCode}`)
      translationError.value = 'Translation API not configured. Please set up AWS Lambda endpoint.'
      
      // For now, fall back to English
      this.i18n.global.locale.value = 'en'
      return
    }

    isTranslating.value = true
    translationError.value = null

    try {
      if (import.meta.env.DEV) console.log(`🌐 Fetching translations for ${languageCode}...`)
      
      // Get the base English translations
      const baseTranslations = this.i18n.global.messages.value.en
      const flattenedEnglish = this.flattenTranslations(baseTranslations)
      
      // Check which translations we already have cached
      let textsToTranslate: Record<string, string> = {}
      const localStorageKey = `wital_translations_${CACHE_VERSION}_${languageCode}`
      const cachedData = localStorage.getItem(localStorageKey)
      
      if (cachedData) {
        try {
          const cachedTranslations = sanitizeMessages(JSON.parse(cachedData), languageCode)
          const flattenedCached = this.flattenTranslations(cachedTranslations)
          
          // Only send texts that are missing from cache
          for (const [key, value] of Object.entries(flattenedEnglish)) {
            if (!flattenedCached[key]) {
              textsToTranslate[key] = value
            }
          }
          
          if (import.meta.env.DEV) console.log(`🔧 Debug: Found ${Object.keys(flattenedEnglish).length} total texts, ${Object.keys(textsToTranslate).length} need translation`)
        } catch (error) {
          console.warn('Failed to parse cached translations, will translate all texts')
          textsToTranslate = flattenedEnglish
        }
      } else {
        // No cache, translate everything
        textsToTranslate = flattenedEnglish
        if (import.meta.env.DEV) console.log(`🔧 Debug: No cache found, translating all ${Object.keys(textsToTranslate).length} texts`)
      }
      
      // If no texts need translation, we're done
      if (Object.keys(textsToTranslate).length === 0) {
        if (import.meta.env.DEV) console.log(`🔧 Debug: All texts already cached, using existing translations`)
        const cachedTranslations = sanitizeMessages(JSON.parse(cachedData!), languageCode)
        this.i18n.global.setLocaleMessage(languageCode, cachedTranslations)
        this.i18n.global.locale.value = languageCode
        return
      }
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000) // 10 second timeout
      })

      // Create the fetch promise - use application/x-www-form-urlencoded to avoid CORS preflight
      const formData = new URLSearchParams()
      formData.append('targetLanguage', languageCode)
      formData.append('texts', JSON.stringify(textsToTranslate))
      
      if (import.meta.env.DEV) {
        console.log(`🔧 Debug: Sending request to ${TRANSLATION_API_URL}`)
        console.log(`🔧 Debug: Language: ${languageCode}`)
        console.log(`🔧 Debug: Form data:`, formData.toString())
      }
      
      const fetchPromise = fetch(TRANSLATION_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      })

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]) as Response

      if (import.meta.env.DEV) {
        console.log(`🔧 Debug: Response status: ${response.status}`)
        console.log(`🔧 Debug: Response headers:`, Object.fromEntries(response.headers.entries()))
      }

      if (!response.ok) {
        // Get the error response body to see what the Lambda function is saying
        const errorText = await response.text()
        if (import.meta.env.DEV) console.log(`🔧 Debug: Error response body:`, errorText)
        throw new Error(`Translation API error: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const data = await response.json()
      if (import.meta.env.DEV) console.log(`🔧 Debug: Response data:`, data)
      const flatTranslations = data.translations || data
      
      // Convert flat translations back to nested structure and sanitize
      const newTranslations = sanitizeMessages(this.unflattenTranslations(flatTranslations), languageCode)
      
      // Merge with existing cached translations if any
      let finalTranslations = newTranslations
      if (cachedData) {
        try {
          const existingTranslations = sanitizeMessages(JSON.parse(cachedData), languageCode)
          finalTranslations = { ...existingTranslations, ...newTranslations }
          if (import.meta.env.DEV) console.log(`🔧 Debug: Merged ${Object.keys(newTranslations).length} new translations with ${Object.keys(existingTranslations).length} existing ones`)
        } catch (error) {
          console.warn('Failed to merge with existing translations, using only new ones')
          finalTranslations = newTranslations
        }
      }

      // Cache the translations in central data store
      dataStore.setTranslations(languageCode, finalTranslations)
      
      // Store in localStorage with timestamp
      localStorage.setItem(localStorageKey, JSON.stringify(finalTranslations))
      localStorage.setItem(`${localStorageKey}_timestamp`, Date.now().toString())

      // Apply to i18n
      this.i18n.global.setLocaleMessage(languageCode, finalTranslations)
      this.i18n.global.locale.value = languageCode

      if (import.meta.env.DEV) console.log(`✅ Translations loaded for ${languageCode}`)
      
    } catch (error) {
      console.error('Failed to fetch translations:', error)
      
      // Provide more user-friendly error messages
      if (error instanceof Error) {
        if (error.message.includes('Request timeout')) {
          translationError.value = 'Translation request timed out. Please try again.'
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          translationError.value = 'Network error. Please check your connection and try again.'
        } else if (error.message.includes('404')) {
          translationError.value = 'Translation service not available. Please try again later.'
        } else if (error.message.includes('500')) {
          translationError.value = 'Translation service error. Please try again later.'
        } else {
          translationError.value = 'Translation failed. Please try again.'
        }
      } else {
        translationError.value = 'Translation failed. Please try again.'
      }
      
      // Fallback to English
      this.i18n.global.locale.value = 'en'
      
    } finally {
      isTranslating.value = false
    }
  }

  // Change language with automatic translation loading
  async changeLanguage(languageCode: string): Promise<void> {
    try {
      await this.loadTranslations(languageCode)
      
      // Save preference
      localStorage.setItem('language', languageCode)
      
      // Update document language attribute
      document.documentElement.lang = languageCode
      
      if (import.meta.env.DEV) console.log(`🌍 Language changed to ${languageCode}`)
      
    } catch (error) {
      console.error('Failed to change language:', error)
      throw error
    }
  }

  // Get current language
  getCurrentLanguage(): string {
    return this.i18n.global.locale.value
  }

  // Set locale directly (for English fallback)
  setLocale(languageCode: string): void {
    this.i18n.global.locale.value = languageCode
    localStorage.setItem('language', languageCode)
    document.documentElement.lang = languageCode
    if (import.meta.env.DEV) console.log(`🌍 Locale set to ${languageCode}`)
  }

  // Flatten nested translations to key-value pairs
  private flattenTranslations(obj: any, prefix = ''): Record<string, string> {
    const flattened: Record<string, string> = {}
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key]
        const newKey = prefix ? `${prefix}.${key}` : key
        
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Recursively flatten nested objects
          Object.assign(flattened, this.flattenTranslations(value, newKey))
        } else if (typeof value === 'string') {
          flattened[newKey] = value
        } else if (Array.isArray(value)) {
          // Handle arrays by converting to indexed keys
          value.forEach((item, index) => {
            if (typeof item === 'string') {
              flattened[`${newKey}.${index}`] = item
            }
          })
        }
      }
    }
    
    return flattened
  }

  // Unflatten translations back to nested structure
  private unflattenTranslations(flattened: Record<string, string>): any {
    const result: any = {}
    
    for (const key in flattened) {
      if (flattened.hasOwnProperty(key)) {
        const value = flattened[key]
        const keys = key.split('.')
        let current = result
        
        for (let i = 0; i < keys.length - 1; i++) {
          const currentKey = keys[i]
          if (!(currentKey in current)) {
            // Check if next key is a number (array index)
            const nextKey = keys[i + 1]
            if (!isNaN(parseInt(nextKey))) {
              current[currentKey] = []
            } else {
              current[currentKey] = {}
            }
          }
          current = current[currentKey]
        }
        
        const lastKey = keys[keys.length - 1]
        if (Array.isArray(current)) {
          current[parseInt(lastKey)] = value
        } else {
          current[lastKey] = value
        }
      }
    }
    
    return result
  }

  // Clear translation cache
  clearCache(): void {
    translationCache.clear()
    
    // Clear localStorage cache
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith('wital_translations_')) {
        localStorage.removeItem(key)
      }
    })
    
    if (import.meta.env.DEV) console.log('🧹 Translation cache cleared')
  }

  // Clear cache for specific language
  clearLanguageCache(languageCode: string): void {
    const cacheKey = `translations_${languageCode}`
    translationCache.delete(cacheKey)
    
    // Clear localStorage cache for specific language
    const localStorageKey = `wital_translations_${languageCode}`
    localStorage.removeItem(localStorageKey)
    localStorage.removeItem(`${localStorageKey}_timestamp`)
    
    if (import.meta.env.DEV) console.log(`🧹 Translation cache cleared for ${languageCode}`)
  }

  // Pre-load common languages for better performance
  async preloadCommonLanguages(): Promise<void> {
    const commonLanguages = ['zh', 'zh-tw', 'zh-hk', 'es', 'fr', 'hi', 'ar', 'pt', 'ru']
    
    for (const lang of commonLanguages) {
      try {
        await this.loadTranslations(lang)
        if (import.meta.env.DEV) console.log(`📚 Pre-loaded ${lang} translations`)
      } catch (error) {
        console.warn(`Failed to pre-load ${lang}:`, error)
      }
    }
  }
}

// Create a singleton instance
let translationService: TranslationService | null = null

export const useTranslationService = (i18nInstance?: any) => {
  if (!translationService && i18nInstance) {
    translationService = new TranslationService(i18nInstance)
  }
  
  return translationService
}

// Global cache clearing functions
export const clearTranslationCache = (): void => {
  if (translationService) {
    translationService.clearCache()
  }
}

export const clearLanguageCache = (languageCode: string): void => {
  if (translationService) {
    translationService.clearLanguageCache(languageCode)
  }
}

// Utility function to translate nested object keys
export const translateNestedObject = async (
  obj: any, 
  targetLanguage: string,
  translationFn: (text: string, targetLang: string) => Promise<string>
): Promise<any> => {
  if (typeof obj === 'string') {
    return await translationFn(obj, targetLanguage)
  }
  
  if (Array.isArray(obj)) {
    return Promise.all(obj.map(item => translateNestedObject(item, targetLanguage, translationFn)))
  }
  
  if (typeof obj === 'object' && obj !== null) {
    const translated: any = {}
    for (const [key, value] of Object.entries(obj)) {
      translated[key] = await translateNestedObject(value, targetLanguage, translationFn)
    }
    return translated
  }
  
  return obj
}

// Language direction helper (for RTL languages)
export const getLanguageDirection = (languageCode: string): 'ltr' | 'rtl' => {
  const rtlLanguages = ['ar', 'he', 'fa', 'ur']
  return rtlLanguages.includes(languageCode) ? 'rtl' : 'ltr'
}

// Language name helper
export const getLanguageName = (languageCode: string, inLanguage?: string): string => {
  const languageNames: Record<string, Record<string, string>> = {
    en: {
      'en': 'English',
      'zh': 'Chinese (Mainland)',
      'zh-tw': 'Chinese (Taiwan)',
      'zh-hk': 'Chinese (Hong Kong)',
      'hi': 'Hindi',
      'es': 'Spanish',
      'fr': 'French',
      'ar': 'Arabic (Standard)',
      'bn': 'Bengali',
      'pt': 'Portuguese',
      'ru': 'Russian'
    },
    zh: {
      'en': '英语',
      'zh': '中文（大陆）',
      'zh-tw': '中文（台灣）',
      'zh-hk': '中文（香港）',
      'hi': '印地语',
      'es': '西班牙语',
      'fr': '法语',
      'ar': '阿拉伯语（标准）',
      'bn': '孟加拉语',
      'pt': '葡萄牙语',
      'ru': '俄语'
    }
  }
  
  const displayLanguage = inLanguage || 'en'
  return languageNames[displayLanguage]?.[languageCode] || languageCode
} 